<template>
  <div v-if="packDataIsLoading">
    <v-row>
      <v-col justify="space-between" cols="12" sm="12">
        <h1 class="font-weight-thin">Pack Summary</h1>
      </v-col></v-row
    >
    <generic-loader caption="Fetching pack data..."></generic-loader>
  </div>
  <div class="px-5" v-else>
    <v-container fluid>
      <v-row>
        <v-col justify="start" sm="12" md="4">
          <h1 class="font-weight-thin">Pack Summary</h1>
        </v-col>
        <v-col sm="12" md="8" align="right">
          <v-btn @click="viewPackList" class="ma-1" color="primary" data-cy="packListLink" outlined small>
            Pack List
          </v-btn>
          <v-btn @click="viewReleaseNotes" class="ma-1" color="primary" data-cy="releaseNoteListLink" outlined small>
            Release Note Summary List
          </v-btn>
          <v-btn @click="navigateToEditForm" class="ma-1" color="primary" data-cy="packEditButton" outlined small>
            Edit Pack
          </v-btn>
        </v-col>
      </v-row>

      <v-card class="px-5 pt-1 elevation-0" color="grey lighten-3">
        <div class="primary--text text--darken-2">
          Pack Type:
          <span data-cy="packType" class="text-uppercase font-weight-bold">{{ pack.type }}</span> / Status:
          <span data-cy="packStatus" class="text-uppercase font-weight-bold">{{ pack.status }}</span>
          <span data-cy="packVersion" v-if="pack.packVersion">
            / Pack Version: <span class="text-uppercase font-weight-bold">{{ pack.packVersion }}</span></span
          >
        </div>
        <div class="font-weight-thin title">{{ pack.title }}</div>
      </v-card>
      <v-row>
        <v-col sm="12" lg="5">
          <v-card class="elevation-0 mt-5" outlined>
            <v-card-text>
              <h2 class="font-weight-thin mb-2">People</h2>
              <div>
                <strong>Pack Lead: </strong>
                {{ usernameForDisplay(pack.packLead) }}
              </div>
              <div v-if="pack.projectManager">
                <strong>Project Manager: </strong>
                {{ usernameForDisplay(pack.projectManager) }}
              </div>
            </v-card-text>
          </v-card>
          <v-card class="elevation-0 mt-5" outlined v-if="pack.parentJira">
            <v-card-text>
              <h2 class="font-weight-thin mb-2">Parent JIRA</h2>
              <a
                :href="`https://jira.ochin.org/browse/${pack.parentJira}`"
                data-cy="parent-jira-link"
                data-test="parent-jira-link"
                target="_blank"
              >
                <span v-text="pack.parentJira" data-cy="parent-jira-link-text" data-test="parent-jira-link">{{
                  pack.parentJira
                }}</span>
              </a>
            </v-card-text>
          </v-card>
          <v-card class="elevation-0 mt-5" outlined>
            <v-card-text>
              <div>
                <h2 class="font-weight-thin mb-2">Pilot Participation</h2>
                <div
                  data-cy="pilotParticipationList-none"
                  data-test="pilotParticipationList-none"
                  v-if="pilotParticipationListIsEmpty"
                >
                  None
                </div>
                <div v-else>
                  <ul data-cy="pilotParticipationList">
                    <li v-for="item in formattedPilotParticipationList" :key="item.id">
                      {{ item.name }} (SA {{ item.id }})
                    </li>
                  </ul>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col sm="12" lg="6">
          <v-card class="elevation-0 mt-5" outlined>
            <v-card-text>
              <div>
                <h2 class="font-weight-thin mb-2">Pack Timeline</h2>
                <pack-timeline :pack="pack"></pack-timeline>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { isEmpty, keyBy } from "lodash";
import GenericLoader from "@/components/GenericLoader";
import PackTimeline from "@/components/PackTimeline";

export default {
  components: { GenericLoader, PackTimeline },
  computed: {
    id() {
      return this.$route.params.id;
    },
    formattedPilotParticipationList() {
      if (!this.pack.pilotParticipationList) {
        return;
      }
      return this.pilotParticipationList.map(serviceAreaId => {
        return this.serviceAreaMap[serviceAreaId];
      });
    },
    pilotParticipationListIsEmpty() {
      return isEmpty(this.pilotParticipationList);
    },
    userMap() {
      return keyBy(this.userList, "username");
    },
    ...mapGetters({
      serviceAreaMap: "admin/serviceArea/getServiceAreaMap",
    }),
    ...mapState({
      pack: state => state.pack.pack,
      pilotParticipationList: state => state.pack.pack.pilotParticipationList,
      serviceAreaList: state => state.admin.serviceArea.list,
      userList: state => state.user.userList,
    }),
  },
  async created() {
    try {
      this.packDataIsLoading = true;
      await this.fetchPackData({ id: this.id });
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "Unable to fetch pack data" });
    }

    try {
      await this.fetchServiceAreaList();
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "Unable to fetch service area list." });
    }

    try {
      await this.fetchUserData();
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "Unable to fetch user data" });
    }

    this.packDataIsLoading = false;
  },
  data() {
    return {
      packDataIsLoading: false,
    };
  },
  methods: {
    navigateToEditForm() {
      this.$router.push({ name: "packEdit", params: { id: this.id } });
    },
    usernameForDisplay(username) {
      const user = this.userMap[username];

      if (!!user) {
        return `${user.firstName} ${user.lastName}`;
      }

      return `${username}`;
    },
    viewPackList() {
      this.$router.push({
        name: "adminPackList",
      });
    },
    viewReleaseNotes() {
      this.$router.push({
        name: "releaseNoteList",
        params: {
          packId: this.id,
        },
        query: {
          page: this.releaseNoteListPage,
        },
      });
    },
    ...mapActions({
      fetchPackData: "pack/getPack",
      fetchServiceAreaList: "admin/serviceArea/fetchList",
      fetchUserData: "user/load",
    }),
  },
};
</script>
